/**
 * Attaches an event listener that will fire once and then autodelete
 *
 * @param el
 * @param eventname
 * @param handler
 */
export function one(el, eventname, handler) {
	if (typeof el === 'undefined' || el === null) {
		return;
	}

	addEventListener(el, eventname, function justOnce() {
		handler();
		removeEventListener(el, eventname, justOnce);
	});
}

export function getData(el, name) {
	if (typeof el === 'undefined' || el === null) {
		return null;
	}

	if (typeof el.dataset === 'undefined') {
		return el.getAttribute('data-' + name);
	}

	return el.dataset[name];
}

export function onDocumentLoaded(handler) {
	addEventListener(window.document, 'DOMContentLoaded', handler);
}

export function onWindowLoadedCompletely(handler) {
	addEventListener(window, 'load', handler);
}

export function addClickListener(el, handler) {
	addEventListener(el, 'click', handler);
}

export function addEventListener(el, eventName, handler) {
	if (typeof el === 'undefined' || el === null) {
		return;
	}

	if (el.addEventListener) {
		el.addEventListener(eventName, handler);
	} else if (el.attachEvent) {
		el.attachEvent('on' + eventName, function () {
			handler.call(el);
		});
	}
}

export function removeEventListener(el, eventName, handler) {
	if (typeof el === 'undefined' || el === null) {
		return;
	}

	if (el.removeEventListener) {
		el.removeEventListener(eventName, handler);
	} else if (el.detachEvent) {
		el.detachEvent('on' + eventName, handler);
	}
}

export function addClass(el, className) {
	if (typeof el === 'undefined' || el === null) {
		return;
	}

	if (el.classList) {
		el.classList.add(className);
	} else {
		el.className += ' ' + className;
	}
}

export function removeClass(el, className) {
	if (typeof el === 'undefined' || el === null) {
		return;
	}

	if (el.classList) {
		el.classList.remove(className);
	} else {
		el.className = el.className.replace(new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'),
			' ');
	}
}

export function show(el, style = 'block') {
	if (typeof el === 'undefined' || el === null) {
		return;
	}

	el.style.display = style;
}

export function hide(el) {
	if (typeof el === 'undefined' || el === null) {
		return;
	}

	el.style.display = 'none';
}

export function hideAll(elementCollection) {
	for (let n = 0; n < elementCollection.length; n++) {
		hide(elementCollection[n]);
	}
}

export function showAll(elementCollection, style = 'block') {
	for (let n = 0; n < elementCollection.length; n++) {
		show(elementCollection[n], style);
	}
}

export function remove(el) {
	if (typeof el === 'undefined' || el === null) {
		return;
	}

	el.parentNode.removeChild(el);
}

export function removeAll(elementCollection) {
	for (let n = elementCollection.length - 1; n >= 0; n--) {
		remove(elementCollection[n]);
	}
}

/**
 * Find the next Sibling to the element. Returns null if there is no other sibling.
 *
 * @param {Node} element
 * @return {Node | null}
 */
export function getNextSibling(element) {
	if (typeof element === 'undefined' || element === null) {
		return null;
	}

	let e = element.nextSibling;
	while (e && 1 !== e.nodeType) {
		e = e.nextSibling;
	}
	return e;
}

export function triggerEvent(el, eventName, data) {
	if (typeof el === 'undefined' || el === null) {
		return;
	}

	let event;

	if (window.CustomEvent) {
		if (typeof window.CustomEvent === 'function') {
			event = new CustomEvent(eventName, {detail: data});
		} else {
			event = document.createEvent('CustomEvent');
			event.initCustomEvent(eventName, false, false, data);
		}
	} else {
		event = document.createEvent('CustomEvent');
		event.initCustomEvent(eventName, true, true, data);
	}

	el.dispatchEvent(event);
}

/**
 * Uses an asynchronous GET for JSON data
 *
 * @param {string} url - base url: do not attach queries!
 * @param {Object} data - an key:value object which will be transformed into a query url
 * @returns {Promise} resolves with the already parsed response text if succesfull, rejects on failure with unparsed responseText
 */
export function getJson(url, data = {}) {
	return new Promise(function (resolve, reject) {
		let request = new XMLHttpRequest();
		let query = Object
			.keys(data)
			.map(function (key) {
				return key + '=' + encodeURIComponent(data[key]);
			})
			.join('&');

		request.open('GET', url + '?' + query, true);

		request.onload = function () {
			if (request.status >= 200 && request.status < 400) {
				resolve(JSON.parse(request.responseText));
			} else {
				reject(request.responseText);
			}
		};

		request.onerror = function () {
			reject(request.responseText);
		};

		request.send();
	});
}

/**
 * Waits for a few seconds before resolving the promise
 *
 * @param {int} seconds
 * @return {Promise}
 */
export function wait(seconds) {
	return new Promise(function (resolve) {
		setTimeout(resolve, seconds * 1000);
	});
}

/**
 * Checks if the document is hidden
 *
 * @return {boolean}
 */
export function isWindowHidden() {
	if (typeof document.hidden !== 'undefined') { // Opera 12.10 and Firefox 18 and later support
		return document.hidden;
	} else if (typeof document['msHidden'] !== 'undefined') {
		return document['msHidden'];
	} else if (typeof document['webkitHidden'] !== 'undefined') {
		return document['webkitHidden'];
	}

	return false;
}

/**
 * Returns the names necessary for listening to visibillitychanges on document
 *
 * pos 0 will be the hidden state to test against
 * pos 1 will be the event listener string to listen against for changes
 *
 * @return {[string,string]}
 */
export function getHiddenDefinition() {
	let hidden, visibilityChange;
	if (typeof document.hidden !== 'undefined') { // Opera 12.10 and Firefox 18 and later support
		hidden = 'hidden';
		visibilityChange = 'visibilitychange';
	} else if (typeof document['msHidden'] !== 'undefined') {
		hidden = 'msHidden';
		visibilityChange = 'msvisibilitychange';
	} else if (typeof document['webkitHidden'] !== 'undefined') {
		hidden = 'webkitHidden';
		visibilityChange = 'webkitvisibilitychange';
	}

	return [hidden, visibilityChange];
}

/**
 * Extends the first object with all following objects, creating a super object
 *
 * Keys in the extendable object will be overwritten by keys in the following objects
 *
 * @param {{}} extendable
 * @return {{}}
 */
export function deepExtend(extendable) {
	extendable = extendable || {};

	for (let i = 1; i < arguments.length; i++) {
		let obj = arguments[i];

		if (!obj) {
			continue;
		}

		for (let key in obj) {
			if (obj.hasOwnProperty(key)) {
				if (typeof obj[key] === 'object') {
					extendable[key] = deepExtend(extendable[key], obj[key]);
				} else {
					extendable[key] = obj[key];
				}
			}
		}
	}

	return extendable;
}

/**
 * Fades an object in
 *
 * @param el
 * @param time
 */
export function fadeIn(el, time = 400) {
	if (typeof el === 'undefined' || el === null) {
		return Promise.resolve(true);
	}

	if (time === 0) {
		el.style.opacity = 1;
		return Promise.resolve(true);
	}

	el.style.opacity = 0;

	return new Promise(resolve => {
		let last = +new Date();
		let tick = function () {
			el.style.opacity = +el.style.opacity + (new Date() - last) / time;
			last = +new Date();

			if (+el.style.opacity < 1) {
				(window.requestAnimationFrame && requestAnimationFrame(tick)) || setTimeout(tick, 16);
			} else {
				resolve(true);
			}
		};

		tick();
	});
}

/**
 *
 * @param {Element} el
 * @param {int} time
 * @return {Promise<any>}
 */
export function fadeOut(el, time = 400) {
	if (typeof el === 'undefined' || el === null) {
		return Promise.resolve(false);
	}

	if (time === 0) {
		el.style.opacity = 0;
		return Promise.resolve(true);
	}

	el.style.opacity = 1;

	return new Promise(resolve => {
		let last = +new Date();
		let tick = function () {
			el.style.opacity = +el.style.opacity - (new Date() - last) / time;
			last = +new Date();

			if (+el.style.opacity > 0) {
				(window.requestAnimationFrame && requestAnimationFrame(tick)) || setTimeout(tick, 16);
			} else {
				resolve(true);
			}
		};

		tick();
	});
}

export function fadeOutAll(elements, time = 400) {
	if (typeof elements === 'undefined' || elements === null) {
		return;
	}

	var promises = [];

	for (var n = 0; n < elements.length; n++) {
		var element = elements[n];
		if (typeof element === 'undefined' || element === null) {
			continue;
		}

		promises.push(fadeOut(element, time));
	}

	return Promise.all(promises);
}

export function fadeInAll(elements, time = 400) {
	if (typeof elements === 'undefined' || elements === null) {
		return;
	}

	var promises = [];

	for (var n = 0; n < elements.length; n++) {
		var element = elements[n];
		if (typeof element === 'undefined' || element === null) {
			continue;
		}

		promises.push(fadeIn(element, time));
	}

	return Promise.all(promises);
}

/**
 * Turns a string into html.
 *
 * Warning: does not work for listelement, or other stuff that does not go natively into a DIV!
 *
 * @param string
 * @return {Node}
 */
export function stringToHtml(string) {
	let wrapper;

	if (string.indexOf('<li') === 0) {
		wrapper = document.createElement('ul');
	} else {
		wrapper = document.createElement('div');
	}
	wrapper.innerHTML = string;
	return wrapper.firstElementChild;
}

/**
 * Checks if an element has a single class
 *
 * Can not check if it has multiple classes!
 *
 * @param el
 * @param className
 * @return {boolean}
 */
export function hasClass(el, className) {
	if (typeof el === 'undefined' || el === null) {
		return false;
	}

	if (el.classList) {
		return el.classList.contains(className);
	}

	return new RegExp('(^| )' + className + '( |$)', 'gi').test(el.className);
}

/**
 * Toggles as single classname on an element
 *
 * @param el
 * @param className
 */
export function toggleClass(el, className) {
	if (typeof el === 'undefined' || el === null) {
		return;
	}

	if (hasClass(el, className)) {
		removeClass(el, className);
	} else {
		addClass(el, className);
	}
}

/**
 * Cross Browser Width and Height checker
 *
 * @return {{width: number, height: number}}
 */
export function getScreenSize() {
	let w = window;
	let d = document;
	let e = d.documentElement;
	let g = d.body || d.getElementsByTagName('body')[0];
	let width = w.innerWidth || e.clientWidth || g.clientWidth;
	let height = w.innerHeight || e.clientHeight || g.clientHeight;

	return {width, height};
}

/**
 * Basically the jQuery finder in simple
 *
 * Returns either a single node (if we are looking for an id) or a NodeList, even if we are only searching for
 * a single thing.
 *
 * @param {String} query
 * @return {Element|NodeList|node}
 */
export function $(query) {
	if (query.match(/^#\w+$/)) {
		return document.getElementById(query.substr(1));
	}

	if (query.match(/^\.\w+$/)) {
		return document.getElementsByClassName(query.substr(1));
	}

	if (query.match(/^\w+$/)) {
		return document.getElementsByName(query);
	}

	return document.querySelectorAll(query);
}

/**
 * Throttle a function so it can be only called once in limit Microseconds
 *
 * @param {Function} func
 * @param {number} limit
 * @return {Function}
 */
export const throttle = (func, limit) => {
	let inThrottle;
	return function () {
		const args = arguments;
		const context = this;
		if (!inThrottle) {
			func.apply(context, args);
			inThrottle = true;
			setTimeout(() => inThrottle = false, limit);
		}
	};
};

/**
 * Debounce a function, so it only is called after delay. Resets delay when called multiple times.
 *
 * @param {Function} func
 * @param {number} delay
 * @return {Function}
 */
export const debounce = (func, delay) => {
	let inDebounce;
	return function () {
		const context = this;
		const args = arguments;
		clearTimeout(inDebounce);
		inDebounce = setTimeout(() => func.apply(context, args), delay);
	};
};

/**
 * Shuffle an array using fisher/yates
 *
 * @param array
 */
export function shuffleArray(array) {
	for (let i = array.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[array[i], array[j]] = [array[j], array[i]];
	}
}

export function setCookie(name, value, days) {
	let expires = '';
	if (days) {
		const date = new Date();
		date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
		expires = '; expires=' + date.toUTCString();
	}
	document.cookie = name + '=' + (value || '') + expires + '; path=/';
}

export function getCookie(name) {
	const nameEQ = name + '=';
	const ca = document.cookie.split(';');
	for (let i = 0; i < ca.length; i++) {
		let c = ca[i];
		while (c.charAt(0) === ' ') {
			c = c.substring(1, c.length);
		}
		if (c.indexOf(nameEQ) === 0) {
			return c.substring(nameEQ.length, c.length);
		}
	}
	return null;
}
